import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "../../../../../../../components/MoreOptionsComponent/MoreOptions";
import { useAppState } from "../../../../../../../context/StateContext";
import { ContainerSize } from "../../../../../../../models/container.size";
import { Job } from "../../../../../../../models/job";
import { Filters, Searches } from "../../../../../../../models/shipeezi.table";
import { Waybill } from "../../../../../../../models/waybill";
import WaybillsService from "../../../../../../../services/waybill.service";
import { calculateAndConvertOrdersVolume } from "../../../../../../../util/calculateAndConvertOrdersVolume";
import {
  ShipmentStatus,
  ShipmentTrackingStatus,
  ShipmentType,
} from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import getRelatedFilters from "../../../../../../../util/getRelatedFilters";
import useStyles from "./styles";

interface ModalAddWaybillProps {
  columns: Column[];
  containerSizes: ContainerSize[];
  onSave: (waybill: Waybill[]) => void;
  waybillsFromOrder: Waybill[];
}

export interface ModalAddWaybillHandle {
  openModal: (alreadyAddedWaybills: Waybill[]) => void;
}

const ModalAddWaybill: ForwardRefRenderFunction<ModalAddWaybillHandle, ModalAddWaybillProps> = (
  props,
  ref
) => {
  const { columns, containerSizes, onSave, waybillsFromOrder } = props;

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const moreOptionsComponentRef = useRef<MoreOptionsComponentHandles>(null);

  const [selectedWaybill, setSelectedWaybill] = useState<Waybill[]>([]);
  const [waybillList, setWaybillList] = useState<Waybill[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [selects, setSelects] = useState<number[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);

  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();

  const [saveDisabled, setSaveDisabled] = useState(false);

  const { currentUser } = useAppState();

  const extraColumns: Column[] = [
    columns[0],
    columns[1],
    columns[2],
    columns[3],
    {
      name: "jobs",
      title: `${translate("volume")} (${getMeasurement()?.code})`,
      formatText: (orders: Job[]) =>
        orders?.length ? calculateAndConvertOrdersVolume(getMeasurement(), orders) : "0",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "jobs",
      title: translate("orders"),
      formatComponent: (orders: Job[]) => (
        <MoreOptions
          ref={moreOptionsComponentRef}
          options={orders?.map((order) => order?.reference ?? "") ?? []}
          title={translate("moreOrders")}
          manyOptionsToShow={2}
        />
      ),
      hideSearch: true,
      hideSort: true,
    },
    ...columns.filter((_, index) => index >= 4),
  ];

  const { isFetching: loadingWayBills } = useQuery(
    ["waybill.list", page, pageSize, searches, filters],
    () =>
      WaybillsService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            association: "jobs",
            include: { association: "itemsJob" },
          },
          { association: "milestones", include: [{ association: "milestone" }] },
          {
            association: "shipmentTracking",
          },
          {
            association: "containers",
            where: Object.values(filters ?? {}).length
              ? getRelatedFilters(filters, "containers")
              : null,
          },
        ]),
        searchByOr: searches && searches?.length ? JSON.stringify(searches) : "",
        ...getRelatedFilters(filters, "shipment"),
      }),
    {
      enabled: !!currentUser?.companyId && open,
      onSuccess: (response) => {
        if (response && response.data) {
          setWaybillList(response.data);
        }
      },
    }
  );

  useQuery(
    ["waybill.count", searches, filters],
    () =>
      WaybillsService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches?.length ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          {
            association: "shipmentTracking",
          },
          {
            association: "containers",
            where: Object.values(filters ?? {}).length
              ? getRelatedFilters(filters, "containers")
              : null,
          },
        ]),
        ...getRelatedFilters(filters, "shipment"),
      }),
    {
      enabled: !!currentUser?.companyId && open,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const onOpenModal = (alreadyAddedWaybills: Waybill[]) => {
    setSelects(alreadyAddedWaybills.map((waybill) => Number(waybill.id)));
    setSelectedWaybill(alreadyAddedWaybills);
    setOpen(true);
  };

  const onCloseModal = () => {
    setSearches([]);
    setFilters(undefined);
    setPage(0);
    setPageSize(20);
    setOpen(false);
  };

  const isAnySubscribedWaybillRemoved = (
    orderWaybills: Waybill[],
    selectedWaybillsIds: number[]
  ) => {
    const removedSubscribedWaybills: Waybill[] = [];

    orderWaybills.forEach((waybill) => {
      if (waybill.id && !selectedWaybillsIds.includes(waybill.id) && waybill.shipmentTracking) {
        removedSubscribedWaybills.push(waybill);
      }
    });

    if (removedSubscribedWaybills.length > 0) return true;

    return false;
  };

  const onAddWaybill = (ids: number[]) => {
    // selected = ids; rows = selects; data = waybillList
    let list: Waybill[] = [];

    if (selects.length > ids.length) {
      // it was removed a row from selected
      list = [...selectedWaybill];
      selectedWaybill.forEach((waybill) => {
        if (waybill.id && !ids.includes(waybill.id)) {
          const waybillRemovedFromSelection = waybillsFromOrder.find(
            (waybillFromOrder) => waybillFromOrder.id === waybill.id
          );

          if (waybillRemovedFromSelection && waybillRemovedFromSelection.shipmentTracking) {
            setSaveDisabled(true);

            NotificationToast.warning(
              translate("aSubscribedShipmentWasUnselectedAndCannotBeRemoved"),
              {
                id: "notification.waybill.remove.subscribed.waybill",
              }
            );
          }

          const index = list.findIndex((item) => item.id === waybill.id);

          list.splice(index, 1);
        }
      });
    } else {
      // it is added a new row to selected
      ids.forEach((id) => {
        if (selects.length > 0) list = [...selectedWaybill];
        const included = list.map((item) => item.id);

        if (!included.includes(id)) {
          const findWaybill = waybillList.find((waybill) => waybill.id === id);

          if (findWaybill) list.push(findWaybill);
        }
      });
    }

    if (!isAnySubscribedWaybillRemoved(waybillsFromOrder, ids)) setSaveDisabled(false);
    setSelectedWaybill(list);
  };

  const onCurrentPage = (numberOfPage: number) => {
    setPage(numberOfPage - 1);
  };

  const onPageSize = (pageSize: number) => setPageSize(pageSize);

  const onSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const onFilters = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  useImperativeHandle(ref, () => ({
    openModal: onOpenModal,
  }));

  return (
    <Modal
      open={open}
      handleClose={onCloseModal}
      title={translate("associateShipmentToOrder")}
      showActionButtons
      className={classes.modal}
      actionButtonProps={{
        onSave: () => {
          onSave(selectedWaybill);
          setSelects([]);
          onCloseModal();
        },
        onCancel: onCloseModal,
        saveDisabled,
        textSave: translate("addShipment"),
      }}
    >
      <div className={classes.container}>
        <ShipeeziNewGrid
          data={waybillList}
          keyExtractor={(data) => data.id}
          columns={extraColumns}
          totalItems={total}
          loadingData={loadingWayBills}
          emptyData={translate("noInformationToBeShown")}
          selecteds={selects}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={onPageSize}
          onNextPage={onCurrentPage}
          onPreviousPage={onCurrentPage}
          onGoToPage={onCurrentPage}
          handleSelect={(rows: number[]) => {
            setSelects(rows);
            onAddWaybill(rows);
          }}
          positionActionsColumn="right"
          generalActions={{
            orderBy: {
              left: ["handleSearch", "handleFilterBy"],
            },
            handleSearch: {
              onSearch: (searches) => onSearch(searches),
            },
            handleFilterBy: {
              categories: [
                {
                  column: "shipment.statusShipment",
                  name: translate("status"),
                  options: [
                    ...Object.values(ShipmentTrackingStatus).map((key) => ({
                      value: key,
                      label: key.charAt(0).toUpperCase() + key.slice(1),
                    })),
                    {
                      value: ShipmentStatus.DRAFT,
                      label:
                        ShipmentStatus.DRAFT.charAt(0).toUpperCase() +
                        ShipmentStatus.DRAFT.slice(1),
                    },
                  ],
                },
                {
                  column: "shipment.shipmentType",
                  name: translate("type"),
                  options: [ShipmentType.SEA, ShipmentType.AIR],
                },
                {
                  column: "containers.size",
                  name: translate("containerSizeType"),
                  options: [
                    ...containerSizes.map((container) => ({
                      label: container.label,
                      value: container.value,
                    })),
                  ],
                },
              ],
              onChange: onFilters,
            },
          }}
        />
      </div>
    </Modal>
  );
};

export default forwardRef(ModalAddWaybill);
